<template>
  <div v-if="campaign.messages_dispatcher" class="dispatch-model-and-product-container">
    <div class="dispatch-model">
      <span class="section-title">Modelo de disparo</span>
      <mf-radio-button
        v-model="model"
        :options="modelOptions"
        :disabled="disabled"
        :error="settingsError.length > 0"
        :error-messages="settingsError"
        mandatory
        show-details-only-on-selected
      >
        <template #extra-label-1>
          <img class="ml-2" :src="require('@/assets/icons/mar.ia-cor.svg')" alt="maria" />
        </template>

        <template #extra-content-1>
          <v-expand-transition>
            <div v-show="model === 'MARIA'" class="extra-content-text">
              <span>Limite máximo de envio por consumidor: </span>
              <inline-select v-if="limiterType === 'WEEKLY'" v-model="limiterQuantity" :options="daysOptions" :disabled="disabled" width="44px" />
              <inline-number-input v-else v-model="limiterQuantity" :limit="3" :disabled="disabled" />
              <span>por</span>
              <inline-select v-model="limiterType" :options="limitPerConsumerOptions" :disabled="disabled" />
            </div>
          </v-expand-transition>
        </template>

        <template #extra-content-2>
          <v-expand-transition>
            <span v-show="model === 'RECURRENT'" class="extra-content-text text-cinza-d10">
              Exemplo:
              <strong class="text-marca-1-d20">Disparo realizado toda segunda-feira</strong>
            </span>
          </v-expand-transition>
        </template>

        <template #extra-content-3>
          <v-expand-transition>
            <span v-show="model === 'STATIC_DATE'" class="extra-content-text text-cinza-d10">
              Exemplo:
              <strong class="text-marca-1-d20">Disparo realizado no dia {{ today }}</strong>
            </span>
          </v-expand-transition>
        </template>
      </mf-radio-button>
    </div>

    <div class="dispatch-products">
      <span class="section-title mb-2">Disparo de diferentes produtos</span>
      <span class="section-description">
        Nos cenários de redisparo de produtos, você pode escolher entre a Mar.ia enviar sempre o melhor produto ou variar entre os melhores produtos para aquele
        consumidor.
      </span>
      <mf-radio-button v-model="productVariableType" :options="productsOptions" :disabled="disabled" mandatory show-details-only-on-selected>
        <template #extra-label-1>
          <img class="ml-2" :src="require('@/assets/icons/mar.ia-cor.svg')" alt="maria" />
        </template>
        <template #description-1>
          <v-expand-transition>
            <span v-show="productVariableType === 'UNREPETED_BEST_PRODUCT'" class="extra-content-text">
              A Mar.ia vai encontrar os melhores produtos para cada consumidor e, a cada novo disparo da campanha, ela selecionará um produto diferente,
              evitando repetições. Assim, seus clientes sempre terão novidades.
            </span>
          </v-expand-transition>
        </template>
        <template #description-2>
          <v-expand-transition>
            <span v-show="productVariableType === 'BEST_PRODUCT'" class="extra-content-text">
              A Mar.ia vai encontrar o melhor produto para cada consumidor e vai enviá-lo sempre, mesmo que ele já tenha sido enviado antes.
            </span>
          </v-expand-transition>
        </template>
      </mf-radio-button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { DateTime } from 'luxon'
import InlineSelect from './InlineSelect.vue'
import InlineNumberInput from './InlineNumberInput.vue'
import { mariaCampaign } from '@/modules/maria/campanha-da-maria-v2/mixins/maria-campaign'

export default {
  name: 'DispatchModelAndProduct',
  components: {
    InlineSelect,
    InlineNumberInput,
  },
  mixins: [mariaCampaign],
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    settingsError: '',
  }),
  computed: {
    ...mapGetters(['campaignDays']),
    today() {
      return DateTime.now().toFormat('dd/MM/yyyy')
    },
    model: {
      get() {
        return this.campaign.messages_dispatcher?.settings?.model
      },
      set(value) {
        this.setOmniCampaignKey({ key: 'messages_dispatcher.settings.model', payload: value })
      },
    },
    productVariableType: {
      get() {
        return this.campaign.messages_dispatcher?.settings?.product_variable_type
      },
      set(value) {
        this.setOmniCampaignKey({ key: 'messages_dispatcher.settings.product_variable_type', payload: value })
      },
    },
    limiterType: {
      get() {
        return this.campaign.messages_dispatcher?.settings?.limit_per_consumer?.limiter
      },
      set(value) {
        this.setOmniCampaignKey({ key: 'messages_dispatcher.settings.limit_per_consumer.limiter', payload: value })
      },
    },
    limiterQuantity: {
      get() {
        return this.campaign.messages_dispatcher?.settings?.limit_per_consumer?.quantity
      },
      set(value) {
        this.setOmniCampaignKey({ key: 'messages_dispatcher.settings.limit_per_consumer.quantity', payload: value })
      },
    },
    modelOptions() {
      return [
        {
          label: 'Mar.ia escolhe',
          value: 'MARIA',
          description: `A Mar.ia escolhe os melhores dias para enviar o disparo para seus consumidores.`,
        },
        {
          label: 'Recorrente por dia da semana',
          value: 'RECURRENT',
          description: `Neste modelo você seleciona o dia da semana em que o disparo é realizado.
          Durante toda a campanha, os disparos serão realizados nesse dia.`,
        },
        {
          label: 'Data Fixa',
          value: 'STATIC_DATE',
          description: `Neste modelo você seleciona exatamente o dia em que o disparo será realizado.`,
        },
      ]
    },
    productsOptions() {
      return [
        {
          label: 'Variar produtos',
          value: 'UNREPETED_BEST_PRODUCT',
          description: `A Mar.ia vai encontrar os melhores produtos para cada consumidor e, a 
          cada novo disparo da campanha, ela selecionará um produto diferente, 
          evitando repetições. Assim, seus clientes sempre terão novidades.`,
        },
        {
          label: 'Enviar sempre o melhor',
          value: 'BEST_PRODUCT',
          description: `A Mar.ia vai encontrar o melhor produto para cada consumidor e vai 
          enviá-lo sempre, mesmo que ele já tenha sido enviado antes.`,
        },
      ]
    },
    daysOptions() {
      return [
        { text: '1x', value: 1 },
        { text: '2x', value: 2 },
        { text: '3x', value: 3 },
        { text: '4x', value: 4 },
        { text: '5x', value: 5 },
        { text: '6x', value: 6 },
        { text: '7x', value: 7 },
      ]
    },
    limitPerConsumerOptions() {
      return [
        { text: 'semana', value: 'WEEKLY' },
        { text: 'campanha', value: 'CAMPAIGN' },
      ]
    },
  },
  watch: {
    model() {
      this.settingsError = ''
      if (this.model === 'MARIA' && !this.campaign.messages_dispatcher?.settings?.limit_per_consumer) {
        this.setOmniCampaignKey({ key: 'messages_dispatcher.settings.limit_per_consumer', payload: { limiter: 'WEEKLY', quantity: 7 } })
      }
    },
    limiterType() {
      this.limiterQuantity = 7
    },
    limiterQuantity() {
      this.settingsError = ''
    },
  },
  created() {
    if (this.itsGetCampaignFromApi && !this.campaign.messages_dispatcher) {
      this.campaign.messages_dispatcher = {
        budget: {
          limiter: 'DAILY', // CAMPAIGN
          channels: {
            sms: 0,
            push: 0,
            email: 0,
            whatsapp: 0,
          },
        },
        settings: {
          campaign_variable_configurations: [],
          model: 'MARIA', // RECURRENT, STATIC_DATE
          limit_per_consumer: {
            limiter: 'WEEKLY', // CAMPAIGN
            quantity: 7,
          },
          product_variable_type: 'UNREPETED_BEST_PRODUCT', // BEST_PRODUCT
          schedule_settings: [],
        },
      }
    }
  },
  methods: {
    validate() {
      this.settingsError = ''
      let isValid = true

      const { settings } = this.campaign.messages_dispatcher
      if (settings.model === 'MARIA') {
        const { limiter, quantity } = settings.limit_per_consumer
        if (limiter === 'CAMPAIGN') {
          if (!quantity) {
            this.settingsError = `Preencher um limite máximo de envio por consumidor é obrigatório para esse modelo de disparo.`
            isValid = false
          } else if (quantity <= 0) {
            this.settingsError = `Esse valor não pode ser 0.`
            isValid = false
          } else if (quantity > this.campaignDays) {
            this.settingsError = `Limite máximo de envio por consumidor não pode ultrapassar ${this.campaignDays} vezes, que é referente a quantidade de dias de campanha.`
            isValid = false
          }
        }
      }

      return isValid
    },
  },
}
</script>

<style lang="scss" scoped>
.dispatch-model-and-product-container {
  margin-top: 48px;
  display: flex;
  flex-direction: row;
  justify-content: space-around;

  .text-cinza-d10 {
    color: $cinza-d10;
  }

  .text-marca-1-d20 {
    color: $marca-1-d20;
  }

  .section-title {
    font-size: 18px;
    font-weight: 700;
    line-height: 21.6px;
    color: $cinza-d20;
  }

  .section-description {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: $cinza-0;
  }

  .dispatch-model,
  .dispatch-products {
    display: flex;
    flex-direction: column;
    width: 566px;
    height: 211px;
  }

  .extra-content-text {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    cursor: auto;
  }

  ::v-deep .label-description-container {
    cursor: auto;
  }

  ::v-deep .label-description-container div:first-of-type {
    cursor: pointer;
  }
}
</style>
